import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiX, FiSearch, FiBook, FiUser, FiCheck, FiLoader, FiZap } = FiIcons;

const NewCheckoutModal = ({ isOpen, onClose, books, users, onCheckout }) => {
  const [loading, setLoading] = useState(false);
  const [bookSearch, setBookSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const availableBooks = useMemo(() => 
    books.filter(b => b.available_copies > 0 && b.status === 'available'),
    [books]
  );

  const filteredBooks = useMemo(() => {
    if (!bookSearch) return availableBooks.slice(0, 5);
    const s = bookSearch.toLowerCase();
    return availableBooks.filter(b => 
      b.title.toLowerCase().includes(s) || b.author.toLowerCase().includes(s)
    ).slice(0, 5);
  }, [availableBooks, bookSearch]);

  const filteredUsers = useMemo(() => {
    if (!userSearch) return users.slice(0, 5);
    const s = userSearch.toLowerCase();
    return users.filter(u => 
      u.full_name?.toLowerCase().includes(s) || u.email.toLowerCase().includes(s)
    ).slice(0, 5);
  }, [users, userSearch]);

  const handleCheckout = async () => {
    if (!selectedBook || !selectedUser) {
      toast.error("Please select both a volume and a member");
      return;
    }
    setLoading(true);
    const res = await onCheckout(selectedBook.id, selectedUser.id);
    setLoading(false);
    if (res.success) {
      toast.success(`Assigned "${selectedBook.title}" to ${selectedUser.full_name}`);
      onClose();
      // Reset state
      setSelectedBook(null);
      setSelectedUser(null);
      setBookSearch('');
      setUserSearch('');
    } else {
      toast.error(res.error);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} className="absolute inset-0 bg-space_indigo/80 backdrop-blur-md" 
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl relative z-10 overflow-hidden border-2 border-white-400 flex flex-col max-h-[90vh]"
        >
          <div className="px-6 py-4 border-b-2 border-white-400 bg-white-500 flex items-center justify-between shrink-0">
            <h2 className="text-lg font-black text-space_indigo uppercase tracking-tight flex items-center">
              <div className="bg-dark_cyan p-2 rounded-xl mr-3">
                <SafeIcon icon={FiZap} className="w-4 h-4 text-white" />
              </div>
              Manual Checkout
            </h2>
            <button onClick={onClose} className="p-2 text-white-300 hover:text-hot_berry transition-colors">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 md:p-8 space-y-8 overflow-y-auto custom-scrollbar">
            {/* Step 1: Volume Selection */}
            <div className="space-y-4">
              <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest ml-1">1. Select Volume</label>
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-white-400 w-4 h-4" />
                <input 
                  type="text" 
                  placeholder="Search available books..." 
                  value={bookSearch}
                  onChange={(e) => setBookSearch(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs" 
                />
              </div>
              <div className="grid grid-cols-1 gap-2">
                {filteredBooks.map(book => (
                  <button 
                    key={book.id}
                    onClick={() => setSelectedBook(book)}
                    className={`flex items-center p-3 rounded-xl border-2 transition-all ${selectedBook?.id === book.id ? 'border-dark_cyan bg-dark_cyan-900 text-dark_cyan' : 'border-white-400 hover:border-white-200 text-white-100'}`}
                  >
                    <SafeIcon icon={FiBook} className="w-4 h-4 mr-3 shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="text-[10px] font-black uppercase truncate">{book.title}</p>
                      <p className="text-[8px] font-bold uppercase text-white-300 truncate">{book.author}</p>
                    </div>
                    {selectedBook?.id === book.id && <SafeIcon icon={FiCheck} className="ml-auto w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Member Selection */}
            <div className="space-y-4">
              <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest ml-1">2. Select Member</label>
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-white-400 w-4 h-4" />
                <input 
                  type="text" 
                  placeholder="Search registered users..." 
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs" 
                />
              </div>
              <div className="grid grid-cols-1 gap-2">
                {filteredUsers.map(user => (
                  <button 
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className={`flex items-center p-3 rounded-xl border-2 transition-all ${selectedUser?.id === user.id ? 'border-hot_berry bg-hot_berry-900 text-hot_berry' : 'border-white-400 hover:border-white-200 text-white-100'}`}
                  >
                    <SafeIcon icon={FiUser} className="w-4 h-4 mr-3 shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="text-[10px] font-black uppercase truncate">{user.full_name || 'Anonymous'}</p>
                      <p className="text-[8px] font-bold uppercase text-white-300 truncate">{user.email}</p>
                    </div>
                    {selectedUser?.id === user.id && <SafeIcon icon={FiCheck} className="ml-auto w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="p-6 border-t-2 border-white-400 bg-white-500 shrink-0">
            <button 
              onClick={handleCheckout}
              disabled={loading || !selectedBook || !selectedUser}
              className="w-full py-4 bg-space_indigo text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl hover:bg-space_indigo-400 disabled:opacity-50 transition-all flex items-center justify-center"
            >
              {loading ? <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" /> : 'Confirm Manual Checkout'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default NewCheckoutModal;